'use client';

import type { Game } from '@/lib/games';
import { avatarOf } from '@/lib/gameAvatars';

/**
 * The thumbnail beside each game's name.
 *
 * Drawn rather than photographed. A screenshot of a game this small is an
 * unreadable smudge — at 40px what survives is one shape, so each of these is
 * the single thing that identifies the game: the snake's turn, the brick wall,
 * the 2048 grid, the flag on the platform.
 *
 * Every one is on the same 40x40 grid with the same weight, so the column reads
 * as one set. The tile is the accent at low opacity and the art is drawn in
 * `currentColor`, which means both follow the theme with no dark-mode variants
 * to keep in step.
 *
 * A game with a `tint` swaps that one pairing for its own hue — the ten vivid
 * games, so the shelf shows what kind of thing is behind each name instead of
 * eighteen identical blue squares. Every class is written out in full below:
 * Tailwind reads the source as text, so `bg-${tint}-200/60` compiles to nothing
 * at all and the tile would come out unpainted.
 */
const TINTS: Record<NonNullable<Game['tint']>, string> = {
  lime: 'bg-lime-200/70 text-lime-700 dark:bg-lime-500/25 dark:text-lime-300',
  orange: 'bg-orange-200/70 text-orange-700 dark:bg-orange-500/25 dark:text-orange-300',
  cyan: 'bg-cyan-200/70 text-cyan-700 dark:bg-cyan-500/25 dark:text-cyan-300',
  pink: 'bg-pink-200/70 text-pink-700 dark:bg-pink-500/25 dark:text-pink-300',
  rose: 'bg-rose-200/70 text-rose-700 dark:bg-rose-500/25 dark:text-rose-300',
  amber: 'bg-amber-200/70 text-amber-700 dark:bg-amber-500/25 dark:text-amber-300',
  violet: 'bg-violet-200/70 text-violet-700 dark:bg-violet-500/25 dark:text-violet-300',
  sky: 'bg-sky-200/70 text-sky-700 dark:bg-sky-500/25 dark:text-sky-300',
  emerald: 'bg-emerald-200/70 text-emerald-700 dark:bg-emerald-500/25 dark:text-emerald-300',
  fuchsia: 'bg-fuchsia-200/70 text-fuchsia-700 dark:bg-fuchsia-500/25 dark:text-fuchsia-300',
  /* The big five, each on a hue no other game uses, so the top shelf reads as
     five different things at a glance. */
  indigo: 'bg-indigo-200/70 text-indigo-700 dark:bg-indigo-500/25 dark:text-indigo-300',
  red: 'bg-red-200/70 text-red-700 dark:bg-red-500/25 dark:text-red-300',
  teal: 'bg-teal-200/70 text-teal-700 dark:bg-teal-500/25 dark:text-teal-300',
  purple: 'bg-purple-200/70 text-purple-700 dark:bg-purple-500/25 dark:text-purple-300',
  yellow: 'bg-yellow-200/70 text-yellow-700 dark:bg-yellow-500/25 dark:text-yellow-300',
  green: 'bg-green-200/70 text-green-700 dark:bg-green-500/25 dark:text-green-300',
  blue: 'bg-blue-200/70 text-blue-700 dark:bg-blue-500/25 dark:text-blue-300',
  /* Black and white, for a game drawn only in greys (Guess Blood Type). */
  graphite: 'bg-neutral-300/70 text-neutral-900 dark:bg-neutral-100/20 dark:text-neutral-100',
};

/**
 * The drawing itself, on a 40x40 grid, with no colour or box of its own.
 *
 * Split out from the chip below because the same marks are wanted at two very
 * different sizes: 24px beside a name in a list, and a hundred-odd pixels as
 * the subject of a cover (GameCover.tsx). Duplicating them would mean every
 * new game had to be drawn twice and the two would drift.
 *
 * `currentColor` throughout, so whatever renders it decides the colour.
 */
export function GameGlyph({ art }: { art: Game['art'] }) {
  return (
    <>
        {art === 'banner' && (
          <>
            <path d="M10 34V6M11 7h23l-5 8 5 8H11M5 34h13" />
            <path d="M16 12h10M16 16h8M16 20h10" strokeWidth="1.5" />
          </>
        )}
        {art === 'castle' && (
          <>
            <path d="M8 34V12h3V8h4v4h4V8h4v4h3v22z" />
            <path d="M15 34v-7a2 2 0 014 0v7" />
            <path d="M28 15l8-9M36 6h-5M36 6v5" />
          </>
        )}
        {art === 'loaf' && (
          <>
            <path d="M6 28c0-8 6-12 14-12s14 4 14 12v3H6z" />
            <path d="M14 20l3 6M20 19l3 6M26 20l3 6" strokeWidth="1.5" />
            <path d="M14 12c-2-2 2-3 0-6M20 12c-2-2 2-3 0-6M26 12c-2-2 2-3 0-6" strokeWidth="1.5" opacity=".7" />
          </>
        )}
        {art === 'sheep' && (
          <>
            <path d="M14 27a5 5 0 01-1-9 6 6 0 0110-4 6 6 0 0110 3 5 5 0 01-1 10z" />
            <circle cx="9.5" cy="20" r="3.6" fill="currentColor" stroke="none" />
            <path d="M17 27v6M28 27v6" />
          </>
        )}
        {art === 'gulp' && (
          <>
            <circle cx="16" cy="20" r="11" />
            <circle cx="13" cy="17" r="1.5" fill="currentColor" stroke="none" />
            <circle cx="19" cy="17" r="1.5" fill="currentColor" stroke="none" />
            <circle cx="21" cy="24" r="2.2" />
            <circle cx="34" cy="24" r="3.4" />
          </>
        )}
        {art === 'bomb' && (
          <>
            {/* A round bomb with a lit fuse — Bomb Squad. No box, unlike minesweeper's mine. */}
            <circle cx="17" cy="23" r="10" fill="currentColor" stroke="none" />
            <path d="M23 15l3-3" strokeWidth="3.2" />
            <path d="M26 12c2-3 5-3 6-6" />
            <path d="M33 4v3M35 7h-3M30 3l1 2" strokeWidth="1.5" opacity=".75" />
          </>
        )}
        {art === 'footprints' && (
          <>
            {/* Two tiptoeing shoe prints — Sneaky Pranks. Not Mischief Row's door-and-bucket. */}
            <ellipse cx="13" cy="24" rx="3.6" ry="5.4" fill="currentColor" stroke="none" />
            <circle cx="13" cy="33" r="2.4" fill="currentColor" stroke="none" />
            <ellipse cx="26" cy="10" rx="3.6" ry="5.4" fill="currentColor" stroke="none" />
            <circle cx="26" cy="19" r="2.4" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'bucket' && (
          <>
            <path d="M8 34V13h13v21" />
            <circle cx="18" cy="24" r="1.2" fill="currentColor" stroke="none" />
            <path d="M19 4l13 3-3 10-10-2z" />
            <path d="M22 21v3M26 24v3M30 21v3" strokeWidth="1.5" opacity=".7" />
          </>
        )}
        {art === 'snake' && (
          <>
            <path d="M8 28h10a5 5 0 000-10h-4a5 5 0 010-10h10" />
            <circle cx="30" cy="8" r="2.2" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'bricks' && (
          <>
            <rect x="6" y="8" width="12" height="6" rx="1.5" />
            <rect x="22" y="8" width="12" height="6" rx="1.5" />
            <rect x="14" y="18" width="12" height="6" rx="1.5" />
            <path d="M12 33h8" strokeWidth="3.4" />
            <circle cx="27" cy="29" r="2" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'grid2048' && (
          <>
            <rect x="6" y="6" width="28" height="28" rx="3" />
            <path d="M6 20h28M20 6v28" />
            <rect x="21" y="21" width="12" height="12" rx="2" fill="currentColor" stroke="none" opacity=".55" />
          </>
        )}
        {art === 'cards' && (
          <>
            <rect x="6" y="9" width="12" height="18" rx="2.5" />
            <rect x="22" y="13" width="12" height="18" rx="2.5" />
            <path d="M28 20v4M26 22h4" />
          </>
        )}
        {art === 'mines' && (
          <>
            {/* A mine in a square, not a sun: the box is what says minesweeper. */}
            <rect x="5" y="5" width="30" height="30" rx="3" opacity=".45" />
            <circle cx="20" cy="21" r="6" fill="currentColor" stroke="none" />
            <path d="M20 11v3M20 28v3M10 21h3M27 21h3M13 14l2 2M25 26l2 2M27 14l-2 2M15 26l-2 2" />
          </>
        )}
        {art === 'runner' && (
          <>
            {/* A figure mid-jump over a gap, with the flag it is heading for. */}
            <path d="M5 32h9M22 32h13" strokeWidth="3.2" />
            <circle cx="15" cy="12" r="2.6" />
            <path d="M15 15v6l-4 5M15 21l4 4" />
            <path d="M31 32V13l6 3-6 3" />
          </>
        )}
        {art === 'bird' && (
          <>
            <rect x="5" y="4" width="7" height="12" rx="2" />
            <rect x="5" y="26" width="7" height="10" rx="2" />
            <rect x="28" y="4" width="7" height="16" rx="2" />
            <circle cx="20" cy="22" r="4" />
            <path d="M24 22l4 1.5-4 1.5" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'noughts' && (
          <>
            <path d="M15 6v28M25 6v28M6 15h28M6 25h28" strokeWidth="2" opacity=".55" />
            <path d="M8 8l4 4M12 8l-4 4" />
            <circle cx="30" cy="30" r="3.4" />
          </>
        )}
        {art === 'blocks' && (
          <>
            {/* An L piece landing on a stack. */}
            <rect x="6" y="6" width="9" height="9" rx="1.5" />
            <rect x="6" y="15" width="9" height="9" rx="1.5" />
            <rect x="15" y="15" width="9" height="9" rx="1.5" />
            <rect x="24" y="26" width="10" height="8" rx="1.5" fill="currentColor" stroke="none" opacity=".5" />
            <rect x="12" y="26" width="10" height="8" rx="1.5" fill="currentColor" stroke="none" opacity=".5" />
          </>
        )}
        {art === 'maze' && (
          <>
            <rect x="6" y="6" width="28" height="28" rx="3" />
            <path d="M13 6v9M13 22v6M20 12v16M27 6v10M27 23v11M6 20h7M20 20h14" />
          </>
        )}
        {art === 'invader' && (
          <>
            {/* The classic silhouette: body, two legs, two eyes. */}
            <path d="M13 12h14v11H13z" />
            <path d="M13 15H9v5h4M27 15h4v5h-4M15 23l-3 5M25 23l3 5" />
            <circle cx="17" cy="17" r="1.3" fill="currentColor" stroke="none" />
            <circle cx="23" cy="17" r="1.3" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'climb' && (
          <>
            <path d="M6 31h11M23 22h11M9 13h13" />
            <circle cx="20" cy="6" r="2.4" />
            <path d="M20 9v5" />
          </>
        )}
        {art === 'mole' && (
          <>
            <path d="M6 30a14 8 0 0128 0" />
            <path d="M14 30v-6a6 6 0 0112 0v6" />
            <circle cx="17" cy="23" r="1.3" fill="currentColor" stroke="none" />
            <circle cx="23" cy="23" r="1.3" fill="currentColor" stroke="none" />
            <path d="M28 8l6 6" strokeWidth="3.4" />
          </>
        )}
        {art === 'echo' && (
          <>
            <path d="M20 6a14 14 0 0114 14H20z" fill="currentColor" stroke="none" opacity=".85" />
            <path d="M20 6A14 14 0 006 20h14z" />
            <path d="M6 20a14 14 0 0014 14V20z" />
            <path d="M34 20a14 14 0 01-14 14V20z" />
          </>
        )}
        {art === 'four' && (
          <>
            <rect x="6" y="10" width="28" height="24" rx="3" />
            <circle cx="13" cy="17" r="2.6" fill="currentColor" stroke="none" />
            <circle cx="20" cy="27" r="2.6" fill="currentColor" stroke="none" />
            <circle cx="27" cy="17" r="2.6" />
            <circle cx="13" cy="27" r="2.6" />
            <path d="M20 4v4" />
          </>
        )}
        {art === 'gem' && (
          <>
            <path d="M20 5l9 8-9 15-9-15z" />
            <path d="M11 13h18M20 5v23" opacity=".6" />
            <circle cx="31" cy="30" r="3.2" fill="currentColor" stroke="none" opacity=".55" />
          </>
        )}
        {art === 'sudoku' && (
          <>
            <rect x="6" y="6" width="28" height="28" rx="2.5" />
            <path d="M15.3 6v28M24.6 6v28M6 15.3h28M6 24.6h28" opacity=".5" strokeWidth="1.7" />
            <path d="M9.6 12.8v-3l-1 .8" strokeWidth="2" />
            <path d="M28.5 30.5h3" strokeWidth="2" />
          </>
        )}
        {art === 'solitaire' && (
          <>
            <rect x="5" y="11" width="13" height="19" rx="2.5" transform="rotate(-9 11 20)" />
            <rect x="20" y="10" width="14" height="20" rx="2.5" />
            <path d="M27 16l3 3-3 4-3-4z" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'paddle' && (
          <>
            <path d="M8 11v12M32 17v12" strokeWidth="3.6" />
            <circle cx="20" cy="20" r="2.6" fill="currentColor" stroke="none" />
            <path d="M20 6v3M20 31v3" opacity=".5" />
          </>
        )}

        {/* ── The vivid ten ──────────────────────────────────────────────── */}

        {art === 'soldier' && (
          <>
            {/* A figure firing right, with the shot already leaving the barrel. */}
            <path d="M5 33h30" strokeWidth="3" opacity=".5" />
            <circle cx="14" cy="10" r="3" />
            <path d="M14 13v9l-4 8M14 22l4 8" />
            <path d="M13 17h11" strokeWidth="3" />
            <circle cx="31" cy="17" r="1.8" fill="currentColor" stroke="none" />
            <circle cx="36" cy="17" r="1.2" fill="currentColor" stroke="none" opacity=".6" />
          </>
        )}
        {art === 'car' && (
          <>
            {/* A road narrowing to a vanishing point — the racer's one image. */}
            <path d="M4 35L16 12M36 35L24 12" />
            <path d="M20 14v4M20 22v5M20 31v4" opacity=".6" />
            <rect x="12" y="26" width="16" height="8" rx="2" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'frog' && (
          <>
            <ellipse cx="20" cy="24" rx="9" ry="7" />
            <circle cx="16" cy="16" r="3" />
            <circle cx="24" cy="16" r="3" />
            <path d="M11 27l-4 5M29 27l4 5" />
            <path d="M6 8h28" opacity=".5" strokeDasharray="4 4" />
          </>
        )}
        {art === 'balloon' && (
          <>
            <ellipse cx="15" cy="14" rx="8" ry="9" />
            <path d="M15 23v5M12 28h6" />
            <circle cx="29" cy="26" r="5" opacity=".65" />
            <path d="M29 31v3" opacity=".65" />
          </>
        )}
        {art === 'fruit' && (
          <>
            {/* Half a fruit, cut face forward, with the blade's line across it. */}
            <path d="M6 26a12 12 0 0124 0z" />
            <path d="M12 26a6 6 0 0112 0" opacity=".6" />
            <path d="M4 30l32-14" strokeWidth="2" opacity=".8" />
          </>
        )}
        {art === 'stack' && (
          <>
            <rect x="9" y="28" width="22" height="6" rx="1.5" />
            <rect x="12" y="21" width="17" height="6" rx="1.5" />
            <rect x="15" y="14" width="13" height="6" rx="1.5" />
            <path d="M20 9V5" opacity=".7" />
            <path d="M17 8l3-3 3 3" opacity=".7" />
          </>
        )}
        {art === 'splat' && (
          <>
            {/* A blot, not a circle: the edge is the whole idea. */}
            <path d="M20 7c6 0 7 5 11 7s3 8-1 10-4 8-10 8-7-4-11-7-3-9 1-11 4-7 10-7z" />
            <circle cx="31" cy="11" r="2" fill="currentColor" stroke="none" />
            <circle cx="9" cy="29" r="1.6" fill="currentColor" stroke="none" opacity=".7" />
          </>
        )}
        {art === 'bubble' && (
          <>
            <circle cx="13" cy="12" r="5.5" />
            <circle cx="25" cy="12" r="5.5" />
            <circle cx="19" cy="22" r="5.5" />
            <path d="M20 34v-5" opacity=".7" />
            <path d="M16 33l4-4 4 4" opacity=".7" />
          </>
        )}
        {art === 'flow' && (
          <>
            <circle cx="9" cy="9" r="3" fill="currentColor" stroke="none" />
            <path d="M9 14v10a4 4 0 004 4h14" />
            <circle cx="31" cy="28" r="3" fill="currentColor" stroke="none" />
            <path d="M31 9H21a4 4 0 00-4 4v3" opacity=".55" />
          </>
        )}
        {art === 'tank' && (
          <>
            {/* Plan view, the way the game draws it: hull, treads, gun forward. */}
            <rect x="9" y="13" width="22" height="16" rx="2" />
            <path d="M9 13V9M31 13V9M9 29v4M31 29v4" strokeWidth="2" opacity=".65" />
            <circle cx="20" cy="21" r="3.4" fill="currentColor" stroke="none" />
            <path d="M20 18V6" strokeWidth="3" />
          </>
        )}
        {art === 'barrage' && (
          <>
            {/* A tank in SIDE view, gun raised, with the shell it just lobbed.
 
                It used to be a hand of cards — the forty-one weapons are the
                thing that makes this game itself, and a tank was avoided
                because `tank` next door (Tanks) already draws one. The cards
                were the wrong call anyway: a shelf of forty-one tiles is
                scanned, not read, and a fan of rectangles at 24px says "card
                game" to somebody who has never opened this one. It is an
                artillery game and it should look like one.
 
                The clash with the sibling is handled by drawing a DIFFERENT
                tank rather than by refusing to draw one. Tanks is a plan view —
                hull from above, treads either side, gun pointing up the screen,
                because that is how that game is played. This is the profile:
                road wheels, a hull you see the flank of, a barrel at the
                elevation you actually aim at, and the arc of the shot leaving
                it. Two tanks, but nobody would mistake one tile for the other,
                and each matches the game behind it. */}
            {/* The redraw (2026-09-12): the first version put three loose
                circles under a plain box, and the cover blows this same drawing
                up to 124px, where a box on wheels reads as a trolley. Three
                changes fix it, and each is a detail a tank silhouette actually
                carries:

                A CLOSED TRACK RUN, not floating wheels. The band around the
                wheels is the single feature that says "tracked" — a big drive
                sprocket at one end, an idler at the other, road wheels between.

                A SLOPED GLACIS. The diagonal off the front of the hull is the
                most recognisable line on a tank, and a rectangle has none.

                A MUZZLE BRAKE. The short tick across the end of the barrel is
                what separates a gun from a pole at 24px, and it is also what
                this game is about — the thing lobbing the shell. */}
            {/* the lobbed shell, arcing away over the muzzle — the barrage */}
            <path d="M32.5 10q3.5-4.5 6.2.6" strokeWidth="1.8" opacity=".55" strokeDasharray="2.6 3" />
            <circle cx="38.4" cy="11.4" r="1.5" fill="currentColor" stroke="none" opacity=".8" />
            {/* barrel at firing elevation, then the brake across its muzzle */}
            <path d="M21.4 17l10.4-6.4" strokeWidth="2.8" />
            <path d="M30.2 12.6l1.6 2.6" strokeWidth="2" />
            {/* turret: sloped at the front, square at the back */}
            <path d="M11.6 20.8V17l2.6-2.4h5.6l1.6 2.2v4z" />
            {/* hull, with the glacis running down to the nose */}
            <path d="M6.6 26.4v-4.2a1.6 1.6 0 011.6-1.6h11.4l8.4 3.4v2.4z" />
            {/* the track run, and the wheels inside it */}
            <path d="M8.6 26.6h19a3.2 3.2 0 010 6.4h-19a3.2 3.2 0 010-6.4z" />
            <circle cx="9.9" cy="29.8" r="1.9" />
            <circle cx="27.1" cy="29.8" r="1.9" />
            <circle cx="15.2" cy="30.4" r="1.15" />
            <circle cx="19" cy="30.4" r="1.15" />
            <circle cx="22.8" cy="30.4" r="1.15" />
          </>
        )}
        {art === 'starship' && (
          <>
            {/* The interceptor, nose up, with its exhaust — the shape the game
                draws at the bottom of the screen. */}
            <path d="M20 6l7 17 5 8-8-2-4 5-4-5-8 2 5-8z" />
            <path d="M20 30v5" strokeWidth="3" opacity=".6" />
            <circle cx="20" cy="16" r="1.8" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'dungeon' && (
          <>
            {/* A corridor in one-point perspective: the whole point of the game
                is that it has a vanishing point. */}
            <path d="M5 6v28M35 6v28" />
            <path d="M5 6l10 9v10L5 34M35 6l-10 9v10l10 9" opacity=".75" />
            <rect x="15" y="15" width="10" height="10" opacity=".55" />
          </>
        )}
        {art === 'turret' && (
          <>
            {/* A road with a bend and one turret covering the inside of it. */}
            <path d="M4 30h12V12h20" strokeDasharray="4 4" opacity=".65" />
            <circle cx="12" cy="20" r="5" />
            <path d="M12 20l7-5" strokeWidth="3" />
            <circle cx="32" cy="26" r="2.2" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'sprinter' && (
          <>
            {/* Mid-stride over a roof edge, with the speed lines behind. */}
            <circle cx="24" cy="9" r="3" />
            <path d="M24 12v9l-6 6M24 21l6 5" />
            <path d="M18 17l-5-3M6 30h11" />
            <path d="M4 14h6M4 20h4" opacity=".5" />
          </>
        )}
        {art === 'racecar' && (
          <>
            {/* Plan view, cornering, with the marks it left. */}
            <rect x="12" y="8" width="16" height="24" rx="5" />
            <path d="M12 14h-4M28 14h4M12 26h-4M28 26h4" strokeWidth="3" />
            <path d="M17 15h6v8h-6z" opacity=".55" />
            <path d="M6 34c6-2 10-5 12-9" strokeDasharray="2 3" opacity=".7" />
          </>
        )}
        {art === 'wand' && (
          <>
            {/* A wand mid-spell: the trail of sparks it leaves. */}
            <path d="M7 33L25 15" strokeWidth="3.2" />
            <path d="M7 33l3-3" strokeWidth="4.4" />
            <path d="M29 6v6M26 9h6" />
            <circle cx="33" cy="17" r="1.6" fill="currentColor" stroke="none" />
            <circle cx="22" cy="8" r="1.3" fill="currentColor" stroke="none" opacity=".7" />
            <path d="M31 24l1.2 2.4 2.4 1.2-2.4 1.2L31 31.2l-1.2-2.4-2.4-1.2 2.4-1.2z" fill="currentColor" stroke="none" opacity=".8" />
          </>
        )}
        {art === 'lane' && (
          <>
            {/* Two towers across one lane, a banner on each. */}
            <path d="M5 33V15h6v18M29 33V15h6v18" />
            <path d="M4 15l4-4 4 4M28 15l4-4 4 4" />
            <path d="M8 11V6l4 1.5L8 9M32 11V6l-4 1.5L32 9" />
            <path d="M3 33h34" strokeWidth="2.8" />
            <path d="M15 28l4-4M25 28l-4-4" opacity=".75" />
          </>
        )}
        {art === 'knight' && (
          <>
            {/* A sword and the ember it is going for. */}
            <path d="M9 31l4-4M7 33l3-3" />
            <path d="M12 28L28 12l4-4 1 5-4 4-16 16-3-1z" />
            <circle cx="12" cy="14" r="3.4" fill="currentColor" stroke="none" opacity=".8" />
            <path d="M12 8v2M8 12h1.5M15 12h1.5" opacity=".55" />
          </>
        )}
        {art === 'fighter' && (
          <>
            <circle cx="14" cy="8" r="3" fill="currentColor" stroke="none" />
            <path d="M14 11v9" />
            <path d="M14 14l9 3" />
            <path d="M14 20l-5 7" />
            <path d="M14 20l7 3" />
            <path d="M5 32h30" strokeWidth="2.8" />
          </>
        )}
        {art === 'coin' && (
          <>
            {/* A coin with the bitcoin mark: B with the two strokes through it. */}
            <circle cx="20" cy="20" r="15.5" />
            <circle cx="20" cy="20" r="12" opacity=".45" />
            <path d="M15.5 13h6.2a3.5 3.5 0 010 7h-6.2zM15.5 20h7a3.5 3.5 0 010 7h-7zM15.5 13v14" strokeWidth="2.4" />
            <path d="M18 10v3M21.5 10v3M18 27v3M21.5 27v3" strokeWidth="2" />
          </>
        )}
        {art === 'orbit' && (
          <>
            {/* One of you in the middle, blades round it, the swarm outside. */}
            <circle cx="20" cy="20" r="4.2" fill="currentColor" stroke="none" />
            <circle cx="20" cy="20" r="10.5" strokeDasharray="3 5" />
            <circle cx="30.5" cy="20" r="2.4" fill="currentColor" stroke="none" />
            <circle cx="9.5" cy="20" r="2.4" fill="currentColor" stroke="none" />
            <path d="M4 8l3 3M36 8l-3 3M4 32l3-3M36 32l-3-3" opacity=".6" />
          </>
        )}
        {art === 'lantern' && (
          <>
            {/* A river lantern over two cards. */}
            <path d="M16 7h8M14.5 10h11l2 13h-15z" />
            <path d="M15 23h10" strokeWidth="2.6" />
            <rect x="8" y="27" width="9" height="11" rx="1.5" transform="rotate(-10 12 32)" opacity=".6" />
            <rect x="23" y="27" width="9" height="11" rx="1.5" transform="rotate(10 28 32)" />
            <circle cx="20" cy="16.5" r="2.2" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'drop' && (
          <>
            {/* A blood drop, half full. */}
            <path d="M20 5C20 5 9 18 9 25a11 11 0 0 0 22 0C31 18 20 5 20 5z" />
            <path d="M9.2 27h21.6a11 11 0 0 1-21.6 0z" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'storm' && (
          <>
            {/* The closing storm ring and a bolt inside it. */}
            <circle cx="20" cy="20" r="14" strokeDasharray="4 3" opacity=".55" />
            <path d="M22 8l-8 13h6l-3 11 9-14h-6z" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'pickaxe' && (
          <>
            {/* A pickaxe biting into a block. */}
            <rect x="20" y="22" width="13" height="12" rx="1.5" />
            <path d="M24 26l3 3M29 25l-2 4" opacity=".55" />
            <path d="M7 33L23 13" strokeWidth="3.2" />
            <path d="M11 9c6-3 13-3 18 1l-3 3c-4-2-9-2-13 0z" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'sweet' && (
          <>
            {/* A gumdrop and a wrapped sweet tumbling down. */}
            <path d="M9 22c-1-8 3-14 8-14s9 6 8 14c-2 2-14 2-16 0z" />
            <path d="M22 26h8l3-3v8l-3-3h-8l-3 3v-8z" opacity=".6" />
            <path d="M13 13c1.5-1 3-1.3 4.5-1" opacity=".5" />
          </>
        )}
        {art === 'gemgrid' && (
          <>
            {/* Three of a kind falling into line. */}
            <path d="M12 6l5 6-5 6-5-6z" />
            <path d="M12 20l5 6-5 6-5-6z" opacity=".55" />
            <path d="M27 20l5 6-5 6-5-6z" />
            <path d="M27 6v7M32 10h-10" opacity=".45" />
          </>
        )}
        {art === 'notes' && (
          <>
            {/* Four lanes and the line they have to cross. */}
            <path d="M6 32h28" strokeWidth="3" />
            <rect x="7" y="10" width="6" height="4" rx="1.5" fill="currentColor" stroke="none" />
            <rect x="16" y="20" width="6" height="4" rx="1.5" fill="currentColor" stroke="none" />
            <rect x="25" y="6" width="6" height="4" rx="1.5" fill="currentColor" stroke="none" />
            <path d="M10 6v2M19 6v10M28 14v14" opacity=".35" />
          </>
        )}
        {art === 'tunnel' && (
          <>
            {/* Rings shrinking to a vanishing point. */}
            <circle cx="20" cy="20" r="14" />
            <circle cx="20" cy="20" r="8.5" opacity=".7" />
            <circle cx="20" cy="20" r="3.5" opacity=".45" />
            <path d="M20 34v-6" strokeWidth="3" />
          </>
        )}
        {art === 'crate' && (
          <>
            <rect x="7" y="7" width="15" height="15" rx="2" />
            <path d="M7 7l15 15M22 7L7 22" strokeWidth="1.8" opacity=".65" />
            <circle cx="30" cy="29" r="5" strokeDasharray="3 3" />
            <path d="M25 12h8" opacity=".5" />
          </>
        )}
        {/* ── The ten of 2026-10-27 (docs/TEN_GAMES_PLAN.md) ── */}
        {art === 'kite' && (
          <>
            {/* A diamond kite, its spars, a bowed tail and the string down to the roof. */}
            <path d="M23 4l10 12-10 12-10-12z" />
            <path d="M23 4v24M13 16h20" strokeWidth="1.5" opacity=".6" />
            <path d="M23 28c-3 2 2 4-1 7" strokeWidth="1.8" />
            <path d="M13 16L5 35" strokeWidth="1.4" strokeDasharray="2 2" opacity=".6" />
          </>
        )}
        {art === 'scroll' && (
          <>
            {/* A hanging scroll: two rollers, the paper, ink mountains and a seal. */}
            <path d="M8 7h24M8 33h24" strokeWidth="3.2" />
            <rect x="11" y="8" width="18" height="24" rx="1" />
            <path d="M14 26l5-7 3 4 2-3 3 6" strokeWidth="1.8" />
            <circle cx="25" cy="13" r="1.8" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'camel' && (
          <>
            {/* A camel walking right: two humps, neck and head, four legs. */}
            <path d="M7 24c1-6 4-9 7-6c1-5 6-5 7 0c2 0 4-1 5 0l3-6h4" />
            <path d="M10 24v8M15 24v8M22 24v8M26 23v9" strokeWidth="2" />
            <path d="M7 24h19" opacity=".5" />
          </>
        )}
        {art === 'domino' && (
          <>
            {/* A two-four domino laid as a sleeper on a rail. */}
            <rect x="6" y="9" width="28" height="15" rx="2.5" />
            <path d="M20 9v15" strokeWidth="1.6" />
            <circle cx="11" cy="13.5" r="1.5" fill="currentColor" stroke="none" />
            <circle cx="15" cy="19.5" r="1.5" fill="currentColor" stroke="none" />
            <circle cx="25" cy="13.5" r="1.5" fill="currentColor" stroke="none" />
            <circle cx="29" cy="13.5" r="1.5" fill="currentColor" stroke="none" />
            <circle cx="25" cy="19.5" r="1.5" fill="currentColor" stroke="none" />
            <circle cx="29" cy="19.5" r="1.5" fill="currentColor" stroke="none" />
            <path d="M4 32h32" strokeWidth="1.8" opacity=".6" />
            <path d="M9 29v6M17 29v6M25 29v6M33 29v6" strokeWidth="1.4" opacity=".5" />
          </>
        )}
        {art === 'deadbolt' && (
          <>
            {/* Two fanned cards, a keyhole on the front one. */}
            <rect x="7" y="9" width="14" height="21" rx="2" transform="rotate(-14 14 19.5)" opacity=".5" />
            <rect x="18" y="9" width="14" height="21" rx="2" transform="rotate(10 25 19.5)" />
            <circle cx="25.3" cy="17.5" r="2.5" />
            <path d="M25.6 20.2l-.4 4.3" strokeWidth="2.2" />
          </>
        )}
        {art === 'bottlecap' && (
          <>
            {/* A crimped bottle cap with a crown pressed on top. */}
            <path d="M20 9.5L21.8 11.6L24.3 10.3L25.3 12.8L28 12.4L28.1 15.2L30.8 15.8L30 18.4L32.3 19.8L30.6 22L32.3 24.2L30 25.6L30.8 28.3L28.1 28.8L28 31.6L25.3 31.2L24.3 33.7L21.8 32.4L20 34.5L18.2 32.4L15.7 33.7L14.7 31.2L12 31.6L11.9 28.8L9.2 28.3L10 25.6L7.7 24.2L9.4 22L7.7 19.8L10 18.4L9.2 15.8L11.9 15.2L12 12.4L14.7 12.8L15.7 10.3L18.2 11.6z" strokeWidth="1.8" />
            <path d="M14 26v-7l3 3 3-4 3 4 3-3v7z" fill="currentColor" stroke="none" opacity=".8" />
          </>
        )}
        {art === 'cue' && (
          <>
            {/* The cue on its stroke, the ball, and the chalk line it will take. */}
            <path d="M5 35L23.5 16.5" strokeWidth="3" />
            <circle cx="28.5" cy="11.5" r="4.5" />
            <path d="M32 8l4-4" strokeWidth="1.6" strokeDasharray="2 2.5" opacity=".6" />
          </>
        )}
        {art === 'quilt' && (
          <>
            {/* Four patches inside a running stitch, one pinned. */}
            <rect x="5" y="5" width="30" height="30" rx="3" strokeWidth="1.6" strokeDasharray="2.6 2.4" opacity=".7" />
            <rect x="9" y="9" width="10" height="10" rx="1" fill="currentColor" stroke="none" />
            <rect x="21" y="21" width="10" height="10" rx="1" fill="currentColor" stroke="none" />
            <rect x="21" y="9" width="10" height="10" rx="1" strokeWidth="1.8" />
            <rect x="9" y="21" width="10" height="10" rx="1" strokeWidth="1.8" />
            <path d="M23.5 11.5l5 5M28.5 11.5l-5 5" strokeWidth="1.4" opacity=".6" />
          </>
        )}
        {art === 'striker' && (
          <>
            {/* A carrom board's corner pockets, a coin, and the striker flicked at it. */}
            <rect x="5" y="5" width="30" height="30" rx="3" opacity=".5" />
            <circle cx="9.5" cy="9.5" r="2.4" fill="currentColor" stroke="none" />
            <circle cx="30.5" cy="9.5" r="2.4" fill="currentColor" stroke="none" />
            <circle cx="9.5" cy="30.5" r="2.4" fill="currentColor" stroke="none" />
            <circle cx="30.5" cy="30.5" r="2.4" fill="currentColor" stroke="none" />
            <circle cx="16" cy="15" r="2.8" fill="currentColor" stroke="none" opacity=".8" />
            <circle cx="22" cy="26" r="4.2" />
            <path d="M20 21.6l-2-3" strokeWidth="1.5" strokeDasharray="1.5 1.8" />
          </>
        )}
        {art === 'crown' && (
          <>
            {/* A crown resting on a cushion rail, a red ball beside it. */}
            <path d="M9 24l2-12 6 6 3-8 3 8 6-6 2 12z" />
            <path d="M5 29h30" strokeWidth="3.2" />
            <circle cx="31" cy="20" r="3" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'typeset' && (
          <>
            {/* Three type sorts on a composing stick, the middle one inked. */}
            <path d="M5 28h30v5H5z" />
            <rect x="7" y="12" width="8" height="14" rx="1" />
            <rect x="16" y="12" width="8" height="14" rx="1" fill="currentColor" stroke="none" opacity=".85" />
            <rect x="25" y="12" width="8" height="14" rx="1" />
            <path d="M9 22l2-6 2 6M9.8 20h2.4" strokeWidth="1.3" />
          </>
        )}
        {/* ── The ten of docs/TEN_GAMES_GIRLS_PLAN.md ── */}
        {art === 'hanger' && (
          <>
            {/* A coat hanger on its hook, and a glint. */}
            <path d="M17 10.5a3 3 0 1 1 3 3v3.5" />
            <path d="M20 17L6.5 27.5c-1.3 1-.6 2.5 1 2.5h25c1.6 0 2.3-1.5 1-2.5z" />
            <path d="M31.5 5.5v4.5M29.2 7.8h4.6" strokeWidth="1.6" opacity=".65" />
          </>
        )}
        {art === 'paw' && (
          <>
            {/* A paw print: the pad and four toes. */}
            <path d="M12.5 27.5c0-4.8 3.4-8.5 7.5-8.5s7.5 3.7 7.5 8.5c0 3-2.6 4.5-7.5 4.5s-7.5-1.5-7.5-4.5z" fill="currentColor" stroke="none" />
            <ellipse cx="9.8" cy="18.5" rx="3" ry="3.6" fill="currentColor" stroke="none" />
            <ellipse cx="15.6" cy="11.5" rx="3" ry="3.9" fill="currentColor" stroke="none" />
            <ellipse cx="24.4" cy="11.5" rx="3" ry="3.9" fill="currentColor" stroke="none" />
            <ellipse cx="30.2" cy="18.5" rx="3" ry="3.6" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'flower' && (
          <>
            {/* Five petals round a seed head, the stem and a leaf. */}
            <circle cx="20" cy="8.6" r="3.6" strokeWidth="2.2" />
            <circle cx="25.2" cy="12.3" r="3.6" strokeWidth="2.2" />
            <circle cx="23.2" cy="18.4" r="3.6" strokeWidth="2.2" />
            <circle cx="16.8" cy="18.4" r="3.6" strokeWidth="2.2" />
            <circle cx="14.8" cy="12.3" r="3.6" strokeWidth="2.2" />
            <circle cx="20" cy="14" r="2.4" fill="currentColor" stroke="none" />
            <path d="M20 22v12" />
            <path d="M20 30c-4.5 0-7.5-2.2-8.5-5.5 4.5 0 7.5 2.2 8.5 5.5z" fill="currentColor" stroke="none" opacity=".8" />
          </>
        )}
        {art === 'treehouse' && (
          <>
            {/* A little house in the crown of a tree, its lit window. */}
            <path d="M10 20c-3.5-.8-4.6-5-1.8-7.4.4-4.2 5-6.2 8-4.4 2.2-3.2 7.6-3.2 9.6.2 3.4-1 7 1.6 6.2 5.2 2.8 2 2 6-1.4 6.6" opacity=".5" />
            <path d="M20 34V24" strokeWidth="3.2" />
            <path d="M13 34h14" strokeWidth="2" />
            <path d="M13 24h14v-6.5L20 12l-7 5.5z" />
            <rect x="18.2" y="18.5" width="3.6" height="3.4" rx=".6" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'horseshoe' && (
          <>
            {/* A horseshoe, heels up, six nail holes. */}
            <path d="M8.5 7.5v11a11.5 11.5 0 0 0 23 0v-11h-5.6v11a5.9 5.9 0 0 1-11.8 0v-11z" strokeWidth="2.2" />
            <circle cx="11.3" cy="11.5" r="1.2" fill="currentColor" stroke="none" />
            <circle cx="11.3" cy="17" r="1.2" fill="currentColor" stroke="none" />
            <circle cx="28.7" cy="11.5" r="1.2" fill="currentColor" stroke="none" />
            <circle cx="28.7" cy="17" r="1.2" fill="currentColor" stroke="none" />
            <circle cx="13.6" cy="23.8" r="1.2" fill="currentColor" stroke="none" />
            <circle cx="26.4" cy="23.8" r="1.2" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'magnifier' && (
          <>
            {/* A magnifying glass with a glint on the lens. */}
            <circle cx="17" cy="17" r="9.5" />
            <path d="M24 24l9 9" strokeWidth="4.2" />
            <path d="M12.5 15a5.2 5.2 0 0 1 4-4" strokeWidth="1.8" opacity=".6" />
            <path d="M32 5.5v5M29.5 8h5" strokeWidth="1.6" opacity=".65" />
          </>
        )}
        {art === 'diary' && (
          <>
            {/* A camp diary: a heart on the cover, the strap, a strip of washi tape. */}
            <rect x="9" y="6" width="22" height="28" rx="2.6" />
            <path d="M14 6v28" strokeWidth="1.8" opacity=".55" />
            <path d="M31 17h-4.5v6H31" strokeWidth="2.2" />
            <path d="M21 15.2c-1.5-2.2-5-1.1-3.7 1.7L21 20.8l3.7-3.9c1.3-2.8-2.2-3.9-3.7-1.7z" fill="currentColor" stroke="none" />
            <path d="M7.5 11.5l7.5-2.8" strokeWidth="3.4" opacity=".45" />
          </>
        )}
        {art === 'confetti' && (
          <>
            {/* A party popper going off: a streamer and confetti. */}
            <path d="M7 33.5l6.2-17.3 11.1 11.1z" />
            <path d="M10.4 24.2l5.4 5.4" strokeWidth="1.6" opacity=".55" />
            <path d="M18.5 14.5c.8-3.6 3.8-4.6 5.8-2.7 2 1.9 4.8.4 4.8-2.6" strokeWidth="1.9" />
            <path d="M23 7.5l1.6-2.6M30.5 15.5l3.6-.8M31 22.5l2.6 2" strokeWidth="2.2" />
            <circle cx="33.2" cy="8.4" r="1.5" fill="currentColor" stroke="none" />
            <circle cx="26.2" cy="19.6" r="1.5" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'skate' && (
          <>
            {/* A figure skate: the boot, its laces, the blade, and a sparkle of ice. */}
            <path d="M10.5 7.5h8.5v11.5c4.4.2 9 1.4 10.8 4.3 1.3 2.1.3 4.7-2.3 4.7H10.5z" />
            <path d="M13.5 11.5h5.5M13.5 15.5h5.5" strokeWidth="1.6" opacity=".6" />
            <path d="M7.5 33h22.5c2 0 3.6-1 4.2-2.6" strokeWidth="2.4" />
            <path d="M13.5 28v5M25.5 28v5" strokeWidth="2" />
            <path d="M33.5 6.5v6M30.5 9.5h6M31.4 7.4l4.2 4.2M35.6 7.4l-4.2 4.2" strokeWidth="1.3" opacity=".65" />
          </>
        )}
        {art === 'moonstar' && (
          <>
            {/* A crescent moon cradling a star. */}
            <path d="M21.5 6.8A13.5 13.5 0 1 0 33.4 26.6 10.6 10.6 0 0 1 21.5 6.8z" />
            <path d="M27.4 9.2l1.3 2.7 3 .4-2.2 2.1.5 3-2.6-1.4-2.7 1.4.5-3-2.2-2.1 3-.4z" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'baseball' && (
          <>
            {/* A bat across a stitched ball. */}
            <path d="M8 33l17.5-17.5c1.6-1.6 4.4-4.2 6-5.8 1.4-1.4 3.4.6 2 2l-5.8 6L10.2 35.2z" />
            <circle cx="12" cy="12" r="6" />
            <path d="M8.2 9.2c1.6 1 1.6 4.6 0 5.6M15.8 9.2c-1.6 1-1.6 4.6 0 5.6" strokeWidth="1.5" opacity=".65" />
          </>
        )}
        {art === 'firecracker' && (
          <>
            {/* A string of three firecrackers on its fuse, with a spark. */}
            <path d="M20 4v26" strokeWidth="1.6" opacity=".6" />
            <rect x="11" y="10" width="6" height="11" rx="1.4" transform="rotate(-18 14 15.5)" />
            <rect x="23" y="14" width="6" height="11" rx="1.4" transform="rotate(18 26 19.5)" />
            <rect x="16" y="24" width="7" height="12" rx="1.4" fill="currentColor" stroke="none" />
            <path d="M20 4l-2.4-1.6M20 4l2.4-1.6M20 4v-2.6" strokeWidth="1.5" />
          </>
        )}
        {art === 'hanafuda' && (
          <>
            {/* A flower card: a blossom on its branch under a red sun band. */}
            <rect x="10" y="5" width="20" height="30" rx="2" />
            <path d="M13 31c4-3 7-8 8-14" strokeWidth="1.8" />
            <circle cx="22" cy="14" r="2.4" fill="currentColor" stroke="none" />
            <circle cx="17" cy="20" r="1.8" fill="currentColor" stroke="none" />
            <circle cx="25.5" cy="20.5" r="1.6" fill="currentColor" stroke="none" />
            <path d="M13 9h14" strokeWidth="1.6" opacity=".55" />
          </>
        )}
        {art === 'dicetower' && (
          <>
            {/* A dice tower with a die tumbling out of its mouth. */}
            <path d="M9 34V8l5-3 5 3v26z" />
            <path d="M9 15l10 3M9 23l10-3" strokeWidth="1.6" opacity=".6" />
            <rect x="22" y="24" width="10" height="10" rx="2" transform="rotate(-14 27 29)" />
            <circle cx="25" cy="27.6" r="1.1" fill="currentColor" stroke="none" />
            <circle cx="29" cy="30.4" r="1.1" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'dart' && (
          <>
            {/* A dart in the bull of a board. */}
            <circle cx="17" cy="23" r="12" />
            <circle cx="17" cy="23" r="6.5" strokeWidth="1.6" opacity=".6" />
            <circle cx="17" cy="23" r="1.8" fill="currentColor" stroke="none" />
            <path d="M18 22l13-13" strokeWidth="2.2" />
            <path d="M29 6.5l5 .5-.5 5M31 9l3.2-3.2" strokeWidth="1.8" />
          </>
        )}
        {art === 'boule' && (
          <>
            {/* Two pétanque boules and the little cochonnet. */}
            <circle cx="13" cy="24" r="8" />
            <path d="M7 21.5c4 2 8 2 12 0M7 26.5c4-2 8-2 12 0" strokeWidth="1.4" opacity=".55" />
            <circle cx="27" cy="19" r="6.5" />
            <path d="M22.4 17c3 1.6 6.2 1.6 9.2 0" strokeWidth="1.4" opacity=".55" />
            <circle cx="31.5" cy="31" r="2.6" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'curlingstone' && (
          <>
            {/* A curling stone with its handle, sliding. */}
            <path d="M8 26c0-5 5.5-8 12-8s12 3 12 8-5.5 7-12 7-12-2-12-7z" />
            <path d="M8.5 25h23" strokeWidth="1.6" opacity=".55" />
            <path d="M16 18v-4c0-1.6 1.2-2.6 2.8-2.6H30" strokeWidth="2.6" />
            <path d="M3.5 30h3M2.5 25h3" strokeWidth="1.6" opacity=".55" />
          </>
        )}
        {art === 'settebello' && (
          <>
            {/* An Italian card: the seven of coins. */}
            <rect x="9" y="4.5" width="22" height="31" rx="2.4" />
            {[[15, 10], [25, 10], [15, 16.5], [25, 16.5], [20, 21.5], [15, 28], [25, 28]].map(([x, y]) => (
              <circle key={`${x}-${y}`} cx={x} cy={y} r="2.3" fill="currentColor" stroke="none" />
            ))}
          </>
        )}
        {art === 'football' && (
          <>
            {/* A football curling into the top corner of a goal. */}
            <path d="M4 34V9h32v25" />
            <path d="M4 17h32M4 25h32M12 9v25M20 9v25M28 9v25" strokeWidth="1" opacity=".4" />
            <circle cx="29" cy="15" r="4.6" fill="currentColor" stroke="none" />
            <path d="M8 33c6-2 13-7 17-14" strokeWidth="1.8" strokeDasharray="2 2.6" />
          </>
        )}
        {art === 'yut' && (
          <>
            {/* Four yut sticks in the air above the mat. */}
            <path d="M9 30l6-20M15 31l3-21M22 31l1-21M28 30l-1-20" strokeWidth="3.4" />
            <path d="M6 35h28" strokeWidth="1.6" opacity=".55" />
          </>
        )}
        {art === 'cricketbat' && (
          <>
            {/* A cricket bat, a ball and the stumps. */}
            <path d="M10 34l13-13 3 3-13 13zM24 20l6-6" />
            <circle cx="29" cy="28" r="3.2" fill="currentColor" stroke="none" />
            <path d="M28 6v10M32 6v10M36 6v10M27 6h10" strokeWidth="1.6" opacity=".6" />
          </>
        )}
        {art === 'durak' && (
          <>
            {/* Two cards, one beating the other. */}
            <rect x="8" y="8" width="15" height="22" rx="2" transform="rotate(-12 15 19)" />
            <rect x="18" y="11" width="15" height="22" rx="2" transform="rotate(12 25 22)" />
            <path d="M25 18l2.5 4-2.5 4-2.5-4z" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'kubb' && (
          <>
            {/* The kubb king with his crown and a spinning baton. */}
            <path d="M14 34V14h12v20zM14 14l2-4 4 3 4-3 2 4" />
            <path d="M28 8l8 8" strokeWidth="3" />
            <path d="M5 34h30" strokeWidth="1.6" opacity=".55" />
          </>
        )}
        {art === 'surfboard' && (
          <>
            {/* A board on a curling wave. */}
            <path d="M4 30c8-18 26-22 32-8-6-4-14-2-16 8" />
            <path d="M12 26l18-10" strokeWidth="3.2" />
          </>
        )}
        {art === 'mancala' && (
          <>
            {/* The squares of Ô ăn quan with stones, a half-moon mandarin at each end. */}
            <path d="M10 13h20v14H10zM10 20h20M15 13v14M20 13v14M25 13v14" strokeWidth="1.6" />
            <path d="M10 13a7 7 0 0 0 0 14M30 13a7 7 0 0 1 0 14" />
            <circle cx="6" cy="20" r="2" fill="currentColor" stroke="none" />
            <circle cx="17.5" cy="16.5" r="1.1" fill="currentColor" stroke="none" />
            <circle cx="22.5" cy="23.5" r="1.1" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'truco' && (
          <>
            {/* A Spanish-suit card with a sword, and a mate gourd. */}
            <rect x="7" y="6" width="17" height="26" rx="2" />
            <path d="M15.5 10v16M12.5 13h6" strokeWidth="1.8" />
            <path d="M27 34c-3 0-4-3-4-6s2-5 5-5 5 2 5 5-1 6-4 6zM30 23l3-11" />
          </>
        )}
        {art === 'loteria' && (
          <>
            {/* A lotería card: a sun, with a bean on it. */}
            <rect x="9" y="5" width="22" height="30" rx="2" />
            <circle cx="20" cy="17" r="5" />
            <path d="M20 8v2M20 24v2M11 17h2M27 17h2M14 11l1.4 1.4M24.6 21.6L26 23M26 11l-1.4 1.4M15.4 21.6L14 23" strokeWidth="1.6" />
            <ellipse cx="25" cy="29" rx="2.4" ry="1.6" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'pelota' && (
          <>
            {/* A pelota ball and the curved xistera basket. */}
            <path d="M8 32c0-12 6-22 20-26 2 10-4 20-16 26z" />
            <circle cx="28" cy="27" r="5" />
            <path d="M24.5 24c2 2 5 2 7 0" strokeWidth="1.4" opacity=".6" />
          </>
        )}
        {art === 'sjoel' && (
          <>
            {/* A sjoelbak: four gates and a disc sliding in. */}
            <path d="M6 10h28M6 10v22h28V10" />
            <path d="M12 10v6M18 10v6M24 10v6M30 10v6" strokeWidth="1.8" />
            <ellipse cx="20" cy="26" rx="4" ry="2.4" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'hockeystick' && (
          <>
            {/* Two crossed sticks and a puck on the ice. */}
            <path d="M9 6l14 24h7M31 6L17 30h-7" strokeWidth="2.6" />
            <ellipse cx="20" cy="35" rx="5" ry="2" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'handball' && (
          <>
            {/* A handball with its panels, flying at the goal. */}
            <circle cx="16" cy="22" r="9" />
            <path d="M8 19c5 2 11 2 16 0M12 14c2 5 2 11 0 16M20 14c-2 5-2 11 0 16" strokeWidth="1.5" />
            <path d="M28 8h8v14M28 8v4" strokeWidth="1.8" opacity=".6" />
          </>
        )}
        {art === 'schnapsen' && (
          <>
            {/* Twenty cards fanned with a gold 66. */}
            <rect x="7" y="10" width="13" height="20" rx="2" transform="rotate(-15 13 20)" />
            <rect x="14" y="9" width="13" height="20" rx="2" />
            <rect x="21" y="10" width="13" height="20" rx="2" transform="rotate(15 27 20)" />
            <circle cx="20.5" cy="19" r="3" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'wrestle' && (
          <>
            {/* Two oiled wrestlers locked chest to chest. */}
            <circle cx="13" cy="9" r="3.5" />
            <circle cx="27" cy="9" r="3.5" />
            <path d="M13 13l7 8 7-8M20 21l-6 13M20 21l6 13" strokeWidth="2.4" />
            <path d="M5 35h30" strokeWidth="1.6" opacity=".55" />
          </>
        )}
        {art === 'rueda' && (
          <>
            {/* Dancers in a ring, holding hands. */}
            <circle cx="20" cy="21" r="11" strokeDasharray="4 3" />
            <circle cx="20" cy="8" r="2.6" fill="currentColor" stroke="none" />
            <circle cx="31" cy="25" r="2.6" fill="currentColor" stroke="none" />
            <circle cx="9" cy="25" r="2.6" fill="currentColor" stroke="none" />
            <path d="M17 19l3-3 3 3" strokeWidth="1.6" />
          </>
        )}
        {art === 'spikes' && (
          <>
            {/* A running spike and the lane lines. */}
            <path d="M6 26c6 0 10-6 14-10l10 4c3 1 4 4 4 6H6z" />
            <path d="M10 30v3M16 30v3M22 30v3M28 30v3" strokeWidth="1.8" />
            <path d="M4 10h12M8 15h8" strokeWidth="1.6" opacity=".55" />
          </>
        )}
        {art === 'falcon' && (
          <>
            {/* A falcon in its stoop, wings swept back. */}
            <path d="M20 34L14 16l-9-6 12 2 3-6 3 6 12-2-9 6z" />
            <circle cx="20" cy="10" r="1.3" fill="currentColor" stroke="none" />
            <path d="M6 32c4-2 6-6 6-6M34 32c-4-2-6-6-6-6" strokeWidth="1.5" opacity=".55" />
          </>
        )}
        {art === 'rugbyball' && (
          <>
            {/* A rugby ball with its laces, over the try line. */}
            <ellipse cx="20" cy="18" rx="13" ry="8" transform="rotate(-25 20 18)" />
            <path d="M15 20l10-5M17 16l1.5 2.5M20 14.5l1.5 2.5M23 13l1.5 2.5" strokeWidth="1.5" />
            <path d="M4 34h32" strokeWidth="2" opacity=".6" />
          </>
        )}
        {art === 'hurley' && (
          <>
            {/* A hurley and the sliotar in the air. */}
            <path d="M10 6l12 22c1 3 6 6 10 4 2-1 1-4-2-5l-4-1L14 5" />
            <circle cx="30" cy="10" r="3.4" />
            <path d="M28 8.5c1 1 3 2 4 3" strokeWidth="1.2" />
          </>
        )}
        {art === 'hoop' && (
          <>
            {/* A street hoop with its net and the ball dropping in. */}
            <path d="M8 6h24v10H8zM14 16h12" />
            <path d="M14 16l2 10h8l2-10M17 16l1 10M23 16l-1 10" strokeWidth="1.5" />
            <circle cx="20" cy="32" r="4" fill="currentColor" stroke="none" />
          </>
        )}
        {art === 'twintrees' && (
          <>
            {/* Two trees from one seed, side by side, one with a nest. */}
            <path d="M12 34V20M12 24l-4-4M12 22l4-5M28 34V19M28 23l5-4M28 21l-4-5M6 34h28" />
            <circle cx="12" cy="13" r="6" />
            <circle cx="28" cy="12" r="6.5" />
            <path d="M30 19.5h4" strokeWidth="2" />
          </>
        )}
    </>
  );
}

export default function GameArt({ art, tint, id }: { art: Game['art']; tint?: Game['tint']; id?: string }) {
  const avatar = id ? avatarOf(id) : null;
  return (
    <span
      aria-hidden
      className={
        'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ' +
        (tint
          ? TINTS[tint]
          : 'bg-accent-200/60 text-accent-700 dark:bg-accent-700/35 dark:text-accent-300')
      }
    >
      {avatar ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={avatar} alt="" aria-hidden draggable={false} loading="lazy" className="h-9 w-9 object-contain" />
      ) : (
        <svg viewBox="0 0 40 40" className="h-6 w-6" fill="none" stroke="currentColor"
             strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
          <GameGlyph art={art} />
        </svg>
      )}
    </span>
  );
}
